import React, { memo, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';

type EmptyStateProps = {
	image: {
		src: string;
	};
	Illustration?: React.ElementType;
	messages: string[];
	learnMore?: {
		message: string;
		url: string;
	};
};

const EmptyState = ({ image: { src }, messages, learnMore, Illustration }: EmptyStateProps) => {
	const onClick = (event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'learnMoreLink');
	};

	return (
		<Container>
			{Illustration && isVisualRefreshEnabled() && (
				<Illustration
					height={gridSize * 10}
					width={gridSize * 10}
					alt=""
					testId="software-backlog-panel.common.ui.panel.empty-state.illustration"
				/>
			)}
			{(!Illustration || !isVisualRefreshEnabled()) && (
				<img
					data-testid="software-backlog-panel.common.ui.panel.empty-state.illustration"
					src={src}
					height={gridSize * 10}
					width={gridSize * 10}
					alt=""
				/>
			)}
			<Messages>
				{messages.map((message, index) => (
					// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
					<p key={index}>{message}</p>
				))}
			</Messages>
			{learnMore && (
				// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
				<p>
					<Button
						testId="software-backlog-panel.common.ui.panel.empty-state.button"
						appearance="link"
						href={learnMore.url}
						target="_blank"
						onClick={onClick}
					>
						{learnMore.message}
					</Button>
				</p>
			)}
		</Container>
	);
};

export { EmptyState };

export default memo<EmptyStateProps>(EmptyState);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	marginTop: token('space.600'),
	marginRight: token('space.200'),
	marginBottom: token('space.800'),
	marginLeft: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Messages = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	marginTop: token('space.400'),
	textAlign: 'center',

	color: token('color.text.subtlest'),
});
